import { BellRing } from 'lucide-react'
import { type ReactNode, useState, useSyncExternalStore } from 'react'
import { toast } from 'sonner'

import {
  getNotificationPermission,
  NotificationPermissionState,
  requestNotificationPermission,
  subscribeToNotificationPermission,
} from '../../lib/desktopNotifications'
import { cn } from '../../utils/cn'
import { Banner } from './banner'
import { Button } from './button'

const DISMISSED_KEY_PREFIX = 'cai-notifications-prompt-dismissed:'

function readDismissed(dismissKey: string): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY_PREFIX + dismissKey) === 'true'
  } catch {
    return false
  }
}

function writeDismissed(dismissKey: string): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY_PREFIX + dismissKey, 'true')
  } catch {
    // A full or blocked localStorage just brings the offer back on the next visit.
  }
}

interface NotificationsOfferProps {
  /** What the notifications are for, in a sentence. */
  children: ReactNode
  onTurnOn: () => void
  onDismiss: () => void
  className?: string
}

/** The offer to turn on desktop notifications; NotificationsPrompt decides when it shows. */
export function NotificationsOffer({ children, onTurnOn, onDismiss, className }: NotificationsOfferProps) {
  return (
    <Banner
      variant="info"
      small
      align="center"
      icon={<BellRing className="animate-bell-ring-loop" />}
      className={className}
      action={
        <Button size="sm" onClick={onTurnOn}>
          Turn on notifications
        </Button>
      }
      onDismiss={onDismiss}
    >
      {children}
    </Banner>
  )
}

interface NotificationsPromptProps {
  /** What the notifications are for, in a sentence. */
  children: ReactNode
  /** Remembers a dismissal in this browser, so the offer stays away on later visits. */
  dismissKey: string
  /** Where the browser's answer landed once the reader clicked to turn them on. */
  onAnswered?: (permission: NotificationPermissionState) => void
  className?: string
}

/**
 * Offers desktop notifications until the reader turns them on, blocks them or dismisses the offer.
 * Renders nothing where the browser can't show them, and is hidden below md: phone browsers can't deliver them.
 */
export function NotificationsPrompt({
  children,
  dismissKey,
  onAnswered,
  className,
}: NotificationsPromptProps) {
  const permission = useSyncExternalStore(
    subscribeToNotificationPermission,
    getNotificationPermission,
    () => NotificationPermissionState.UNSUPPORTED
  )
  const [dismissed, setDismissed] = useState(() => readDismissed(dismissKey))

  if (permission !== NotificationPermissionState.DEFAULT || dismissed) return null

  async function turnOn() {
    const answer = await requestNotificationPermission()
    onAnswered?.(answer)
    if (answer === NotificationPermissionState.GRANTED) toast.success('Notifications are on.')
  }

  return (
    <NotificationsOffer
      className={cn('max-md:hidden', className)}
      onTurnOn={() => void turnOn()}
      onDismiss={() => {
        writeDismissed(dismissKey)
        setDismissed(true)
      }}
    >
      {children}
    </NotificationsOffer>
  )
}
